import i18next from 'i18next'
import z from 'zod'

import { emptySelectOptionZod, SelectOptionZod, selectOptionZod } from 'components/Select'
import { CountryCode } from 'features/globalSetup/types/country'
import { CurrencyCode } from 'features/globalSetup/types/currency'
import { MAX_SAFE_GQL_INTEGER } from 'providers/tanstack-query/constants'
import { zNumericIntegerString } from 'providers/zod/types'
import { MAX_PROPOSAL_ITEMS } from './constants'
import { GamOrderRetargetingDealsDeliverySlice } from './hooks/useGetGamOrderRetargetingDealsDelivery'
import { calculateLineItems, MAX_LINE_ITEMS } from './lineItems'

const proposalIdItem = z.object({
  lineItems: z.number().optional(),
  maxPrice: z.number().gte(0).multipleOf(0.01),
  minPrice: z.number().gte(0).multipleOf(0.01),
  proposalId: zNumericIntegerString.min(1).max(MAX_SAFE_GQL_INTEGER),
  step: z.number().multipleOf(0.01).gt(0),
})

export type ProposalIdItem = z.infer<typeof proposalIdItem>

export const proposalItemIdDefault: ProposalIdItem = {
  lineItems: 0,
  maxPrice: 5,
  minPrice: 1,
  proposalId: '',
  step: 0.03,
}

export const schema = z
  .object({
    currency: selectOptionZod<CurrencyCode>().refine(
      (value: SelectOptionZod<CurrencyCode>): boolean => value.value !== '',
      i18next.t('common:form.error.nonEmpty'),
    ),
    geo: selectOptionZod<CountryCode>().refine(
      (value: SelectOptionZod<CountryCode>): boolean => value.value !== '',
      i18next.t('common:form.error.nonEmpty'),
    ),
    proposalsData: z.array(proposalIdItem),
  })
  .superRefine((arg, ctx) => {
    arg.proposalsData.forEach((item, index) => {
      // Unique Proposal IDs
      if (
        arg.proposalsData.some(
          ({ proposalId: otherProposalId }, searchIndex) =>
            otherProposalId !== '' && otherProposalId === item.proposalId && searchIndex !== index,
        )
      ) {
        ctx.addIssue({
          code: 'custom',
          message: i18next.t(
            'features/globalSetup:googleAdManagerConnection.setupRetargetingDeliveryItems.form.proposalIdItem.proposalId.errorUnique',
          ),
          path: ['proposalsData', index, 'proposalId'],
        })
      }

      // Proposal items limit
      if (arg.proposalsData.length > MAX_PROPOSAL_ITEMS) {
        ctx.addIssue({
          code: 'custom',
          message: i18next.t(
            'features/globalSetup:googleAdManagerConnection.setupRetargetingDeliveryItems.form.proposalIdItem.errorLimit',
          ),
          path: ['proposalsData', index, 'proposalId'],
        })
      }

      // Min/Max Price
      if (item.minPrice >= item.maxPrice) {
        ctx.addIssue({
          code: 'custom',
          message: i18next.t(
            'features/globalSetup:googleAdManagerConnection.setupRetargetingDeliveryItems.form.proposalIdItem.minPrice.errorMinMax',
          ),
          path: ['proposalsData', index, 'minPrice'],
        })
      }
      if (item.maxPrice <= item.minPrice) {
        ctx.addIssue({
          code: 'custom',
          message: i18next.t(
            'features/globalSetup:googleAdManagerConnection.setupRetargetingDeliveryItems.form.proposalIdItem.maxPrice.errorMinMax',
          ),
          path: ['proposalsData', index, 'maxPrice'],
        })
      }

      // Line Items limit
      if (
        calculateLineItems({
          max: item.maxPrice,
          min: item.minPrice,
          step: item.step,
        }) > MAX_LINE_ITEMS
      ) {
        ctx.addIssue({
          code: 'custom',
          message: i18next.t(
            'features/globalSetup:googleAdManagerConnection.setupRetargetingDeliveryItems.form.proposalIdItem.lineItems.errorLimit',
          ),
          path: ['proposalsData', index, 'lineItems'],
        })
      }
    })
  })

export type Schema = z.infer<typeof schema>

export const schemaDefault = (
  gamOrderRetargetingDealsDelivery?: GamOrderRetargetingDealsDeliverySlice,
): Schema => {
  if (gamOrderRetargetingDealsDelivery === undefined) {
    return {
      currency: emptySelectOptionZod<CurrencyCode>(),
      geo: emptySelectOptionZod<CountryCode>(),
      proposalsData: [proposalItemIdDefault],
    }
  }

  const { currency, geo, proposalsData } = gamOrderRetargetingDealsDelivery

  return {
    currency: currency
      ? {
          label: currency,
          value: currency,
        }
      : emptySelectOptionZod<CurrencyCode>(),
    geo: geo
      ? {
          label: geo,
          value: geo,
        }
      : emptySelectOptionZod<CountryCode>(),
    proposalsData,
  }
}
